import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';

import { languagesHelper as language } from '../../../../util/languages';
import { LandscapesCarousel, LandscapesGrid } from '../../../../components';
import landscapesLogo from '../../../../assets/WILDER_PLACES_logo_1.png';
import css from './SectionColumns.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionColumns = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
    landingPageData,
    pageId
  } = props;

  let currentLocale = language.get();
  const sectionLocale = sectionId?.indexOf('_locale_') !== -1 && sectionId?.slice(sectionId.length - 2)

  if (!!sectionLocale && sectionLocale !== currentLocale) return null;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const sectionsWithCorner = [
    "landscape-map",
    "our-other-landscapes",
    "about-us-contact",
    'section-with-corners-white'
  ]

  const isLandingPage = pageId === "landing-page";
  const isRewildingLandscapes = sectionId === "rewilding-landscapes" || sectionId === "landscapes-carousel";
  const isAllLandscapes = sectionId === "rewilding-europes-landscapes";

  const showCorner = sectionsWithCorner.includes(sectionId);

  const landscapesBlocks = isRewildingLandscapes ? blocks : [];
  const allLandscapesBlocks = isAllLandscapes ? blocks : [];

  const landingPageLandscapes = landingPageData?.landingPage?.data?.sections
    ?.find(s => s.sectionId === 'rewilding-landscapes')?.blocks?.filter(b => !pageId.includes(b.blockId))

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {showCorner && (
        <>
          <div id="leftCorner" />
          <div id="rightCorner" />
        </>
      )}
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <div className={defaultClasses.titleHolder}>
            <Field data={title} className={defaultClasses.title} options={fieldOptions} />
            {isLandingPage && isRewildingLandscapes && <img src={landscapesLogo} alt="Landscapes logo" />}
          </div>
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks && !isRewildingLandscapes && !isAllLandscapes ? (
        <div
          className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            ctaButtonClass={defaultClasses.ctaButton}
            blocks={blocks}
            sectionId={sectionId}
            responsiveImageSizes={getResponsiveImageSizes(numColumns)}
            options={options}
          />
        </div>
      ) : null}
      {isRewildingLandscapes && (
        <div className={css.homepageListings}>
          <LandscapesCarousel
            landscapes={landingPageLandscapes || landscapesBlocks}
            theme={isLandingPage ? "dark" : "light"}
          />
        </div>
      )}
      {isAllLandscapes && (
        <div className={css.homepageListings}>
          <LandscapesGrid landscapes={allLandscapesBlocks} />
        </div>
      )}
    </SectionContainer>
  );
};

export default SectionColumns;
